'use client'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useTeamReplicache } from '../use-team-replicache'

import { useUserStore } from '~/app/_store/user-store'
import { useUserTeamsList } from './use-user-teams-list'
import type { getActivePublicTeam } from '~/app/api/ad-hoc/get-user-teams/actions'

type ActivePublicTeamContext = {
  data: { team: PublicTeam; repo: PublicRepo | null } | undefined
  isInitialLoading: boolean
  isLoading: boolean
}

const ActivePublicTeamContext = React.createContext<
  ActivePublicTeamContext | undefined
>(undefined)

export const ActivePublicTeamContextProvider = ({
  children,
  team,
  repo = null,
}: {
  children?: React.ReactNode
  team?: PublicTeam
  repo?: PublicRepo | null
}) => {
  const isLoadingUser = useUserStore((s) => s.isLoading)
  const { isLoading: isLoadingTeamsList } = useUserTeamsList()
  const { data: teamReplicache, isLoading: isLoadingTeamReplicache } =
    useTeamReplicache()
  const { teamSlug, repoSlug } = useDashboardSlugs()

  const enabled =
    !team &&
    !isLoadingUser &&
    !isLoadingTeamsList &&
    !isLoadingTeamReplicache &&
    !teamReplicache &&
    Boolean(teamSlug)

  // Should only do the fetch when replicache is not available
  const query = useQuery(
    ['active-public-team', teamSlug, repoSlug],
    async () => {
      if (!teamSlug) throw new Error("Shouldn't be enabled")
      const response = (await fetch(
        `/api/ad-hoc/get-active-public-team?teamSlug=${teamSlug}&repoSlug=${repoSlug}`
      ).then((res) => res.json())) as
        | ReturnType<typeof getActivePublicTeam>
        | { error: string }
      if ('error' in response) {
        throw new Error(response.error)
      }
      return response
    },
    {
      enabled,
      initialData: team ? { team, repo } : undefined,
      keepPreviousData: false,
    }
  )

  return (
    <ActivePublicTeamContext.Provider
      value={{
        ...query,
        isInitialLoading: team ? false : query.isInitialLoading,
        isLoading: team ? false : query.isLoading,
      }}
    >
      {children}
    </ActivePublicTeamContext.Provider>
  )
}

export const useActivePublicTeam = () => {
  const context = React.useContext(ActivePublicTeamContext)
  if (!context) {
    throw new Error(
      'useActivePublicTeam must be used within a ActivePublicTeamContextProvider'
    )
  }
  return context
}
